/**
 * "Your basics" is a window over the page the citizen is on, and the URL says
 * so: `?basics=form` (type the missing details), `?basics=document` (scan a
 * passport or ID) or `?basics=import` (a LinkedIn PDF or CV). A link can open
 * it, Back closes it, and a reload keeps it open where it was.
 */
import { safeInternalNext } from '@/features/platform/first-run';

export const PROFILE_BASICS_PARAM = 'basics';

export const PROFILE_BASICS_VIEWS = ['form', 'document', 'import'] as const;
export type ProfileBasicsView = (typeof PROFILE_BASICS_VIEWS)[number];

export function isProfileBasicsView(value: unknown): value is ProfileBasicsView {
  return PROFILE_BASICS_VIEWS.some((view) => view === value);
}

/** The view a search string opens, or `null` when the window is closed. */
export function profileBasicsViewFrom(search: string): ProfileBasicsView | null {
  const value = new URLSearchParams(search).get(PROFILE_BASICS_PARAM);
  return isProfileBasicsView(value) ? value : null;
}

/** `search` with the window set to `view` — or closed, for `null`. Other params are kept. */
export function withProfileBasics(search: string, view: ProfileBasicsView | null): string {
  const params = new URLSearchParams(search);
  if (view) params.set(PROFILE_BASICS_PARAM, view);
  else params.delete(PROFILE_BASICS_PARAM);
  const next = params.toString();
  return next ? `?${next}` : '';
}

/** A link to `path` with "Your basics" open over it. */
export function profileBasicsHref(path: string, view: ProfileBasicsView = 'form'): string {
  return `${path}${withProfileBasics('', view)}`;
}

/**
 * The old focused page, `/profile/complete?return=…`: the same window, over the
 * page it would have returned to (a same-origin path only — Home otherwise).
 */
export function legacyProfileCompleteTarget(search: string): string {
  const back = safeInternalNext(new URLSearchParams(search).get('return')) ?? '/dashboard';
  const [path, query = ''] = back.split('?');
  return `${path}${withProfileBasics(query, 'form')}`;
}
